import { useState } from "react";
import { repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useScaffoldMarketplace } from "@/api/mutations/repoScaffold";

// #276 (UC-1): „Marketplace anlegen" — legt ein EIGENES Repo mit gültigem Katalog an und
// registriert es in einem Schritt.
//
// ⚠ Nicht zu verwechseln mit „Marketplace einbinden" (#232): das registriert nur einen
// Zeiger auf eine FREMDE Quelle, die schon existiert. Der Unterschied entscheidet, ob wir
// den Inhalt verwalten können — deshalb steht er im Dialog, nicht nur im Ticket.

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (marketplaceId: string) => void;
}

const EMPTY = { name: "", repoName: "", description: "" };

export function CreateMarketplaceDialog({ open, onOpenChange, onCreated }: Props) {
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const mutation = useScaffoldMarketplace();
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  function close(o: boolean) {
    if (!o) {
      setForm({ ...EMPTY });
      setError(null);
    }
    onOpenChange(o);
  }

  async function submit() {
    setError(null);
    const name = form.name.trim();
    if (!name) {
      setError("Ein Name ist erforderlich.");
      return;
    }
    try {
      const res = await mutation.mutateAsync({
        name,
        repoName: form.repoName.trim() || undefined,
        description: form.description.trim() || undefined,
      });
      toast.success(`Marketplace „${name}" angelegt`, { description: res.repoFullName });
      onCreated?.(res.marketplaceId);
      close(false);
    } catch (err) {
      // Die Fachfehler des Backends sind aussagekräftig — sie durchreichen ist besser als
      // ein generisches „fehlgeschlagen", das den Admin raten lässt.
      // EINE Fehlercode-Tabelle für alle Repo-Dialoge (lifecycleVocab) — vorher hatte
      // jeder Dialog eine eigene Ternary-Kette mit teils anderen Sätzen für dieselben
      // Codes (Review-Fund #8: sechs driftende Tabellen).
      setError(repoErrorMessage(err, "Anlegen fehlgeschlagen"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="dialog-create-marketplace">
        <DialogHeader>
          <DialogTitle>Marketplace anlegen</DialogTitle>
          <DialogDescription>
            Legt ein eigenes Repository mit leerem, gültigem Katalog an und registriert es. Für
            einen bereits bestehenden, fremden Katalog nutze „Marketplace einbinden".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="mkt-name">Name</Label>
            <Input
              id="mkt-name"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="team-plugins"
              data-testid="input-marketplace-name"
            />
            <p className="text-[12px] text-text3">
              Kleinbuchstaben mit Bindestrichen. Unter diesem Namen sprechen die Agenten den
              Katalog an.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mkt-repo">Repository-Name (optional)</Label>
            <Input
              id="mkt-repo"
              value={form.repoName}
              onChange={(e) => set({ repoName: e.target.value })}
              placeholder={form.name.trim() || "wie der Name"}
              data-testid="input-marketplace-repo"
            />
            <p className="text-[12px] text-text3">Leer lassen — dann wird der Name verwendet.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mkt-desc">Beschreibung (optional)</Label>
            <Input
              id="mkt-desc"
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Wofür ist dieser Katalog da?"
            />
          </div>

          {error && (
            <p className="text-[13px] text-danger" data-testid="create-marketplace-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={mutation.isPending || !form.name.trim()}
            data-testid="submit-create-marketplace"
          >
            {mutation.isPending ? "Lege an …" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
